import type { WalletBalance } from '../../types/wallet'
import type { AllocationSimulationResponse } from '../../types/simulation'
import SwapForm from '../trade/SwapForm'
import TokenIcon from '../common/TokenIcon'
import { MINTS } from '../../api/trade'

interface Props {
  result: AllocationSimulationResponse | null
  balances: WalletBalance[] | null
  onNext: () => void
  onBack: () => void
}

export function SwapStep({ result, balances, onNext, onBack }: Props) {
  const stakeStage = result?.allocation.stake

  return (
    <div className="space-y-5">
      {/* Hero */}
      <div>
        <h2 className="text-lg font-bold text-white mb-1">Get your SOL ready</h2>
        <p className="text-sm text-gray-400">
          Before you can stake, you need SOL in your wallet. Swap any token you already have.
        </p>
      </div>

      {/* Flow diagram */}
      <div className="rounded-xl border border-white/5 bg-surface-card p-5">
        <div className="flex items-center justify-center gap-5">
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
              <span className="text-lg font-bold text-gray-300">$</span>
            </div>
            <span className="text-xs text-gray-400">Any token</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <svg viewBox="0 0 40 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-10 h-4 text-gray-500">
              <line x1="2" y1="8" x2="32" y2="8" />
              <polyline points="26 3 32 8 26 13" />
            </svg>
            <span className="text-[9px] text-gray-600 whitespace-nowrap">Jupiter Swap</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 rounded-xl bg-accent-purple/10 border border-accent-purple/20 flex items-center justify-center">
              <TokenIcon symbol="SOL" mint={MINTS.SOL} size={28} />
            </div>
            <span className="text-xs text-white font-medium">SOL</span>
          </div>
          {stakeStage && (
            <>
              <div className="flex flex-col items-center gap-1">
                <svg viewBox="0 0 40 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-10 h-4 text-gray-500">
                  <line x1="2" y1="8" x2="32" y2="8" />
                  <polyline points="26 3 32 8 26 13" />
                </svg>
                <span className="text-[9px] text-gray-600 whitespace-nowrap">Step 2: Stake</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <TokenIcon symbol="jupSOL" mint={MINTS.jupSOL} size={28} />
                </div>
                <span className="text-xs text-emerald-400 font-medium">jupSOL</span>
              </div>
            </>
          )}
        </div>
      </div>


      {/* Swap form */}
      <div className="rounded-xl border border-white/5 bg-surface-card p-4">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Swap now</div>
        <SwapForm
          balances={balances}
          defaultInputMint={MINTS.USDC}
          defaultOutputMint={MINTS.SOL}
          defaultAmount={stakeStage && stakeStage.amountUsd > 0 ? stakeStage.amountUsd.toFixed(2) : undefined}
        />
      </div>

      {/* Navigation */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-white/10 text-gray-300 text-sm hover:bg-white/5 transition-colors"
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-3.5 h-3.5">
            <polyline points="10 3 5 8 10 13" />
          </svg>
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-accent-purple text-white font-semibold text-sm hover:opacity-90 transition-opacity"
        >
          I have SOL — Next: Stake
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-4 h-4">
            <polyline points="6 3 11 8 6 13" />
          </svg>
        </button>
      </div>
    </div>
  )
}
